import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Download, Save, Settings2, Users, Utensils, Banknote } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { gestionCarrera, guardarEventoCarrera, confirmarPagoManualCarrera, reenviarCorreoCarrera } from "@/lib/carrera.functions";
import {
  participanteSchema,
  leerParticipantes,
  modalidadLabels,
  euro,
  codigoCompra,
  type EventoCarrera,
} from "@/lib/carrera.shared";
export const Route = createFileRoute("/_authenticated/gestion/san-silvestre")({
  loader: () => gestionCarrera(),
  head: () => ({
    meta: [
      { title: "Gestión de San Silvestre · El Pajar de Benito" },
      { name: "description", content: "Inscripciones, pagos y configuración de la San Silvestre." },
      { property: "og:title", content: "Gestión de San Silvestre" },
      { property: "og:description", content: "Panel privado de inscripciones de la carrera." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Gestion,
});
function Gestion() {
  const { evento, rows } = Route.useLoaderData();
  const [settings, setSettings] = useState(false);
  const [draft, setDraft] = useState<EventoCarrera>(evento);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("todos");
  const [mode, setMode] = useState("todos");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const save = useServerFn(guardarEventoCarrera);
  const confirmar = useServerFn(confirmarPagoManualCarrera);
  const reenviar = useServerFn(reenviarCorreoCarrera);
  async function accion(id: string, tipo: "confirmar" | "reenviar") {
    if (tipo === "confirmar" && !confirm("¿Confirmas que el banco te ha enviado el correo de «Operación autorizada» para esta compra? Se marcará como pagada y se enviarán los correos."))
      return;
    setError("");
    setNotice("");
    setBusy(true);
    try {
      if (tipo === "confirmar") await confirmar({ data: { id } });
      else await reenviar({ data: { id } });
      await router.invalidate();
      setNotice(tipo === "confirmar" ? "Marcada como pagada y correos enviados" : "Correo reenviado");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo completar");
    } finally {
      setBusy(false);
    }
  }
  const router = useRouter();
  const people = (json: unknown) => leerParticipantes(json);
  const paid = rows.filter((r) => r.estado === "pagada");
  const all = paid.flatMap((r) => people(r.participantes));
  const filtered = rows.filter(
    (r) =>
      (status === "todos" || r.estado === status) &&
      (mode === "todos" || people(r.participantes).some((p) => p.modalidad === mode)) &&
      `${codigoCompra(r.id)} ${r.contacto_nombre} ${r.email} ${people(r.participantes)
        .map((p) => `${p.nombre} ${p.grupo} ${p.fecha_nacimiento}`)
        .join(" ")}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  function csv() {
    const esc = (value: unknown) =>
      `"${String(value ?? "")
        .replace(/^[=+@-]/, "'$&")
        .replace(/"/g, '""')}"`;
    const header = [
      "Referencia",
      "Nombre",
      "Fecha de nacimiento",
      "Modalidad",
      "Grupo",
      "Menor",
      "Tutor",
      "Contacto",
      "Email",
      "Teléfono",
      "Estado",
      "Total compra",
    ];
    const body = filtered.flatMap((r) =>
      people(r.participantes).map((p) => [
        codigoCompra(r.id),
        p.nombre,
        p.fecha_nacimiento,
        modalidadLabels[p.modalidad],
        p.grupo,
        p.menor ? "Sí" : "No",
        p.tutor,
        r.contacto_nombre,
        r.email,
        r.telefono,
        r.estado,
        r.total,
      ]),
    );
    const blob = new Blob(
      ["\uFEFF" + [header, ...body].map((row) => row.map(esc).join(";")).join("\r\n")],
      { type: "text/csv;charset=utf-8;" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inscripciones-san-silvestre.csv";
    a.click();
    URL.revokeObjectURL(url);
  }
  async function submit() {
    setError("");
    setNotice("");
    setBusy(true);
    try {
      await save({ data: draft });
      await router.invalidate();
      setNotice("Configuración guardada");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl">San Silvestre</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {evento.inscripciones_abiertas && evento.validacion_legal
              ? "Inscripciones abiertas"
              : "Inscripciones cerradas"}{" "}
            · {evento.fecha}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSettings((v) => !v)}>
            <Settings2 />
            Configuración
          </Button>
          <Button variant="outline" onClick={csv}>
            <Download />
            CSV
          </Button>
        </div>
      </div>
      <div className="my-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: "Corredores / andarines",
            value: `${all.filter((p) => p.modalidad === "corredor").length} / ${all.filter((p) => p.modalidad === "andarin").length}`,
            icon: Users,
          },
          {
            label: "Acompañantes",
            value: all.filter((p) => p.modalidad === "acompanante").length,
            icon: Users,
          },
          { label: "Comidas confirmadas", value: all.length, icon: Utensils },
          {
            label: "Cobrado",
            value: euro(paid.reduce((s, r) => s + Number(r.total), 0)),
            icon: Banknote,
          },
        ].map((item) => (
          <div key={item.label} className="rounded-md border bg-card p-5">
            <item.icon className="h-5 w-5 text-primary" />
            <p className="mt-3 text-xs text-muted-foreground">{item.label}</p>
            <p className="mt-2 text-2xl font-semibold">{item.value}</p>
          </div>
        ))}
      </div>
      <p className="mb-6 text-sm text-muted-foreground">
        {rows.filter((r) => r.estado === "pendiente").length} compras pendientes de pago ·{" "}
        {paid.length} compras confirmadas
      </p>
      {settings && (
        <section className="mb-10 border-y py-6">
          <h3 className="font-serif text-xl">Datos del evento</h3>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            {(
              [
                "titulo",
                "fecha",
                "salida",
                "cierre_meta",
                "recorrido",
                "distancia_km",
                "precio_participante",
                "precio_acompanante",
                "organizador",
              ] as const
            ).map((k) => (
              <div key={k}>
                <Label htmlFor={`setting-${k}`}>
                  {
                    {
                      titulo: "Nombre del evento",
                      fecha: "Fecha",
                      salida: "Salida",
                      cierre_meta: "Cierre de meta",
                      recorrido: "Recorrido",
                      distancia_km: "Distancia (km)",
                      precio_participante: "Corredores y andarines (€)",
                      precio_acompanante: "Acompañantes (€)",
                      organizador: "Organiza",
                    }[k]
                  }
                </Label>
                <Input
                  className="mt-2"
                  id={`setting-${k}`}
                  type={
                    k === "fecha"
                      ? "date"
                      : k === "salida" || k === "cierre_meta"
                        ? "time"
                        : k === "distancia_km" || k.startsWith("precio_")
                          ? "number"
                          : "text"
                  }
                  step="0.01"
                  value={draft[k]}
                  onChange={(e) =>
                    setDraft((v) => ({
                      ...v,
                      [k]:
                        k === "distancia_km" || k.startsWith("precio_")
                          ? Number(e.target.value)
                          : e.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>
          <h3 className="mt-8 font-serif text-xl">Condiciones y privacidad</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Completa y valida estos textos antes de abrir el pago. Identifica al responsable legal
            del evento y del cobro, no solo al alojamiento.
          </p>
          <div className="mt-5 grid gap-5">
            {(
              ["responsable_legal", "condiciones", "privacidad", "autorizacion_menores"] as const
            ).map((k) => (
              <div key={k}>
                <Label htmlFor={k}>
                  {
                    {
                      responsable_legal:
                        "Responsable legal y del cobro: razón social, NIF, dirección y contacto",
                      condiciones: "Condiciones, cancelaciones y devoluciones",
                      privacidad: "Información de privacidad y conservación de datos",
                      autorizacion_menores: "Condiciones de participación de menores",
                    }[k]
                  }
                </Label>
                <Textarea
                  id={k}
                  className="mt-2 min-h-24"
                  maxLength={10000}
                  value={draft[k]}
                  onChange={(e) => setDraft((v) => ({ ...v, [k]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <label className="mt-5 flex items-center gap-3 text-sm">
            <Checkbox
              checked={draft.validacion_legal}
              onCheckedChange={(v) => setDraft((d) => ({ ...d, validacion_legal: v === true }))}
            />
            He revisado y aprobado la información legal y de menores.
          </label>
          <label className="mt-4 flex items-center gap-3 text-sm">
            <Checkbox
              checked={draft.inscripciones_abiertas}
              onCheckedChange={(v) =>
                setDraft((d) => ({ ...d, inscripciones_abiertas: v === true }))
              }
            />
            Abrir inscripciones y pago online
          </label>
          <Button className="mt-6" disabled={busy} onClick={submit}>
            <Save />
            {busy ? "Guardando…" : "Guardar configuración"}
          </Button>
          {error && (
            <p role="alert" className="mt-4 text-sm text-destructive">
              {error}
            </p>
          )}
          {notice && (
            <p role="status" className="mt-4 text-sm text-primary">
              {notice}
            </p>
          )}
        </section>
      )}
      {!settings && error && <p role="alert" className="mb-4 text-sm text-destructive">{error}</p>}
      {!settings && notice && <p role="status" className="mb-4 text-sm text-primary">{notice}</p>}
      <div className="mb-5 flex flex-wrap gap-3">
        <Input
          aria-label="Buscar inscripción"
          placeholder="Buscar código, nombre, email o grupo…"
          className="max-w-sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          aria-label="Estado de pago"
          className="rounded-md border bg-background px-3 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="todos">Todos los estados</option>
          <option value="pagada">Pagadas</option>
          <option value="pendiente">Pendientes</option>
        </select>
        <select
          aria-label="Modalidad"
          className="rounded-md border bg-background px-3 text-sm"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <option value="todos">Todas las modalidades</option>
          {Object.entries(modalidadLabels).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>
      {filtered.length === 0 ? (
        <p className="border-y py-12 text-center text-sm text-muted-foreground">
          No hay inscripciones que mostrar.
        </p>
      ) : (
        <div className="divide-y border-y">
          {filtered.map((r) => (
            <details key={r.id} className="py-4">
              <summary className="cursor-pointer">
                <span className="inline-flex w-[95%] flex-wrap items-center justify-between gap-3 text-sm">
                  <span>
                    <strong>{r.contacto_nombre}</strong>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {codigoCompra(r.id)} · {people(r.participantes).length} personas
                    </span>
                  </span>
                  <span>
                    {r.estado === "pagada" ? "Pagada" : "Pendiente"} · {euro(Number(r.total))}
                  </span>
                </span>
              </summary>
              <div className="mt-4 space-y-3 text-sm">
                <p>
                  {r.email} · {r.telefono}
                </p>
                <p className="text-xs text-muted-foreground">
                  Confirmación por correo: {r.email_enviado_at ? "enviada" : "no enviada"}
                </p>
                <ul className="space-y-2">
                  {people(r.participantes).map((p, i) => (
                    <li key={i}>
                      {p.nombre} · {modalidadLabels[p.modalidad]}
                      {p.fecha_nacimiento ? ` · Nacimiento: ${p.fecha_nacimiento}` : ""}
                      {p.grupo ? ` · Grupo: ${p.grupo}` : ""}
                      {p.menor ? ` · Menor, responsable: ${p.tutor}, autorización registrada` : ""}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2 pt-2">
                  {r.estado === "pendiente" ? (
                    <Button size="sm" disabled={busy} onClick={() => accion(r.id, "confirmar")}>
                      Marcar como pagada y enviar correos
                    </Button>
                  ) : r.estado === "pagada" ? (
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => accion(r.id, "reenviar")}>
                      Reenviar correo al comprador
                    </Button>
                  ) : null}
                </div>
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
